/**
 * Per-model LLM pricing in USD per 1,000,000 tokens.
 *
 * Used to compute a per-message provider-cost snapshot when usage is stored.
 * Analytics reads that immutable snapshot, so catalog changes affect future
 * calls without repricing historical usage. Legacy rows without a snapshot are
 * frozen once at their first post-migration analytics projection.
 *
 * Coverage: current + most-recently-superseded models (Anthropic 4.5–5.5 +
 * Fable 5/5.1, OpenAI GPT-6/6.1 + GPT-5.4–5.6 families, Gemini 3.1–3.8 family,
 * xAI Grok 4.x). Older still-
 * selectable legacy models (e.g. gpt-5.2, gpt-4.1, gemini-2.5-*) and most other
 * OpenRouter-only third-party models have no entry: they return null pricing
 * → cost is recorded as 0 and a one-time warning is emitted. (The Grok entries
 * below are keyed bare, so they also price OpenRouter-routed `x-ai/grok-*`.)
 *
 * Source convention: list prices from each provider's pricing page. Update
 * here when providers publish new rates.
 */

import { fromIso } from '../time';
import type { IsoTimestamp } from '../time';

export interface ModelPricing {
  /** USD/MTok for regular (un-cached) input tokens. */
  input: number;
  /** USD/MTok for output tokens. */
  output: number;
  /** USD/MTok for cache-read input tokens (cache hit). */
  cache_read?: number;
  /** USD/MTok for cache-creation input tokens (Anthropic 5m TTL default; OpenAI's one write rate). */
  cache_write?: number;
  /**
   * USD/MTok for 1h-TTL cache-creation tokens (Anthropic, 2× input vs the 1.25× 5m rate).
   * A provider with a single write rate (OpenAI) repeats `cache_write` here, which keeps the
   * catalog invariant that every `cache_write` row has one (see the tests); `computeCostUsd`
   * prices 1h tokens at `cache_write` when it is omitted.
   */
  cache_write_1h?: number;
  /**
   * The rates for a CALL whose own prompt exceeds `above_prompt_tokens` (Haiku 5.5 bills every
   * bucket at 5× past 100K). Never applied to a stored row's summed usage — a row can sum
   * several calls — only to the `long_prompt` slice {@link longPromptSlice} classified per call.
   */
  long_prompt?: Omit<ModelPricing, 'long_prompt'> & { above_prompt_tokens: number };
}

/**
 * The token counters one call — or a sum of calls — is priced from, in Anthropic's convention:
 * `input_tokens` excludes both cache buckets.
 */
export interface LlmTokenCounts {
  input_tokens?: number;
  output_tokens?: number;
  cache_read_input_tokens?: number;
  cache_creation_input_tokens?: number;
  /**
   * The 1h-TTL portion of `cache_creation_input_tokens` (Anthropic). The 5m
   * portion is the remainder and is never passed separately — see `priceCounts`.
   */
  cache_creation_1h_input_tokens?: number;
}

/** Every counter present, each a finite non-negative number. */
type TokenCounts = Required<LlmTokenCounts>;

/** The summed counters of the calls whose own prompt crossed their model's long-prompt threshold. */
export type LongPromptUsage = TokenCounts;

// Compiler-checked against `LlmTokenCounts`: a counter added there without one here fails to build
// instead of escaping the long-prompt split below.
const COUNTER_KEYS = Object.keys({
  input_tokens: true,
  output_tokens: true,
  cache_read_input_tokens: true,
  cache_creation_input_tokens: true,
  cache_creation_1h_input_tokens: true,
} satisfies Record<keyof LlmTokenCounts, true>) as Array<keyof LlmTokenCounts>;

/**
 * Rates plus a price change the provider has already published: from `next.effective` on, `next`
 * prices the usage instead. A snapshot is frozen when it is computed, so each call keeps the
 * rate that applied then.
 */
export type WithPriceChange<R> = R & { next?: R & { effective: IsoTimestamp } };

/** The rates in force at `atMs`. */
function ratesAt<R>(pricing: WithPriceChange<R>, atMs: number): R {
  return pricing.next && atMs >= fromIso(pricing.next.effective) ? pricing.next : pricing;
}

// Google's Gemini 3.6–3.8 Flash (text and TTS) launch rates hold through 2026-12-31 and double
// from this instant.
const GOOGLE_PRICE_CHANGE: IsoTimestamp = '2027-01-01T00:00:00.000Z';
const GEMINI_FLASH_LAUNCH_RATES: WithPriceChange<ModelPricing> = {
  input: 0.75, output: 3.75, cache_read: 0.075,
  next: { effective: GOOGLE_PRICE_CHANGE, input: 1.50, output: 7.50, cache_read: 0.15 },
};

/**
 * Currently-active model rates. Keys match the bare model name as stored on
 * the agent's settings. OpenRouter prefixes (`anthropic/`, `openai/`, etc.)
 * are stripped before lookup — see `getModelPricing` below.
 */
export const MODEL_PRICING: Record<string, WithPriceChange<ModelPricing>> = {
  // ---- Anthropic ----
  // Rates from platform.claude.com/docs/en/about-claude/pricing. Deprecated-but-
  // selectable models keep their entry so they still bill correctly.
  // Fable 5/5.1 ($10/$50) reason adaptively — no thinking toggle; Opus 4.5–4.8 share one rate card.
  // `cache_write` is the 5m TTL rate (1.25× input); `cache_write_1h` is 2× input. Cache reads are
  // 0.1× input except Fable 5.1 (0.025×) and Opus 5.5 / Sonnet 5.5 (0.05×) — Sonnet 5.5's $0.10 is
  // from its own launch page ("half the Claude Sonnet 5 rate") and the pricing page's multiplier
  // table; the pricing page's per-model row repeats Sonnet 5's $0.20. Sonnet 5's $2/$10 launch rate is
  // its standard rate (the increase scheduled for 2026-09-01 was cancelled). Haiku 5.5 bills every
  // token at 5× once one call's prompt passes 100K (`long_prompt`). Opus 5 is not offered (Opus
  // 5.5 superseded it before it reached the catalog); its row prices custom-value agents.
  'claude-fable-5-1':  { input: 10,   output: 50, cache_read: 0.25, cache_write: 12.5,  cache_write_1h: 20   },
  'claude-fable-5':    { input: 10,   output: 50, cache_read: 1.0,  cache_write: 12.5,  cache_write_1h: 20   },
  'claude-opus-5-5':   { input: 4,    output: 20, cache_read: 0.20, cache_write: 5,     cache_write_1h: 8    },
  'claude-opus-5':     { input: 5,    output: 25, cache_read: 0.5,  cache_write: 6.25,  cache_write_1h: 10   },
  'claude-opus-4-8':   { input: 5,    output: 25, cache_read: 0.5,  cache_write: 6.25,  cache_write_1h: 10   },
  'claude-opus-4-7':   { input: 5,    output: 25, cache_read: 0.5,  cache_write: 6.25,  cache_write_1h: 10   },
  'claude-opus-4-6':   { input: 5,    output: 25, cache_read: 0.5,  cache_write: 6.25,  cache_write_1h: 10   },
  'claude-opus-4-5':   { input: 5,    output: 25, cache_read: 0.5,  cache_write: 6.25,  cache_write_1h: 10   },
  'claude-sonnet-5-5': { input: 2,    output: 10, cache_read: 0.10, cache_write: 2.5,   cache_write_1h: 4    },
  'claude-sonnet-5':   { input: 2,    output: 10, cache_read: 0.20, cache_write: 2.5,   cache_write_1h: 4    },
  'claude-sonnet-4-6': { input: 3,    output: 15, cache_read: 0.30, cache_write: 3.75,  cache_write_1h: 6    },
  'claude-sonnet-4-5': { input: 3,    output: 15, cache_read: 0.30, cache_write: 3.75,  cache_write_1h: 6    },
  'claude-haiku-5-5':  {
    input: 0.10, output: 0.50, cache_read: 0.01, cache_write: 0.125, cache_write_1h: 0.20,
    long_prompt: { above_prompt_tokens: 100_000, input: 0.50, output: 2.50, cache_read: 0.05, cache_write: 0.625, cache_write_1h: 1 },
  },
  'claude-haiku-4-5':  { input: 1,    output: 5,  cache_read: 0.10, cache_write: 1.25,  cache_write_1h: 2    },

  // ---- OpenAI GPT-6.1 + GPT-6 + GPT-5.6 Sol/Terra/Luna families ----
  // Standard-tier rates from developers.openai.com/api/docs/pricing (2026-10), prompts under
  // 272K tokens. Cached input is 0.1× input, except GPT-6.1 Sol (0.05×). GPT-5.6 and newer also
  // bill cache writes, at 1.25× input; OpenAI has one write rate, so `cache_write_1h` repeats it.
  // GPT-5.6 Sol's $4/$20 is promotional "at least through 2026-11-21" with no successor rate
  // published yet.
  'gpt-6.1-sol':   { input: 2,    output: 10,   cache_read: 0.10,  cache_write: 2.5,   cache_write_1h: 2.5   },
  'gpt-6-astra':   { input: 10,   output: 50,   cache_read: 1.00,  cache_write: 12.5,  cache_write_1h: 12.5  },
  'gpt-6-sol':     { input: 2,    output: 10,   cache_read: 0.20,  cache_write: 2.5,   cache_write_1h: 2.5   },
  'gpt-6-luna':    { input: 0.10, output: 0.50, cache_read: 0.01,  cache_write: 0.125, cache_write_1h: 0.125 },
  'gpt-5.6-sol':   { input: 4,    output: 20,   cache_read: 0.40,  cache_write: 5,     cache_write_1h: 5     },
  'gpt-5.6-terra': { input: 2,    output: 12,   cache_read: 0.20,  cache_write: 2.5,   cache_write_1h: 2.5   },
  'gpt-5.6-luna':  { input: 0.20, output: 1.20, cache_read: 0.02,  cache_write: 0.25,  cache_write_1h: 0.25  },

  // ---- OpenAI GPT-5.4/5.5 family ----
  // Standard-tier rates from developers.openai.com/api/docs/pricing (2026-09), prompts under
  // 272K tokens (cached input 0.1× input).
  'gpt-5.5':       { input: 5,    output: 30,   cache_read: 0.50  },
  'gpt-5.4':       { input: 2.50, output: 15,   cache_read: 0.25  },
  'gpt-5.4-mini':  { input: 0.75, output: 4.50, cache_read: 0.075 },
  'gpt-5.4-nano':  { input: 0.20, output: 1.25, cache_read: 0.02  },

  // ---- Google Gemini 3.1–3.8 family ----
  // Paid-tier text rates, prompts ≤200K (ai.google.dev/gemini-api/docs/pricing, 2026-09).
  // Gemini 3.6–3.8 Flash launch at $0.75/$3.75 through 2026-12-31 and double on 2027-01-01.
  // `gemini-3.1-pro` is not a published id (only `-preview` is); its row prices usage
  // already recorded under it.
  'gemini-3.8-flash':       GEMINI_FLASH_LAUNCH_RATES,
  'gemini-3.7-flash':       GEMINI_FLASH_LAUNCH_RATES,
  'gemini-3.6-flash':       GEMINI_FLASH_LAUNCH_RATES,
  'gemini-3.5-flash':       { input: 1.50, output: 9.0,  cache_read: 0.15  },
  'gemini-3.5-flash-lite':  { input: 0.30, output: 2.5,  cache_read: 0.03  },
  'gemini-3.1-pro-preview': { input: 2,    output: 12,   cache_read: 0.20  },
  'gemini-3.1-pro':         { input: 2,    output: 12,   cache_read: 0.20  },
  'gemini-3.1-flash':       { input: 0.30, output: 2.5,  cache_read: 0.075 },
  'gemini-3.1-flash-lite':  { input: 0.25, output: 1.50, cache_read: 0.025 },

  // ---- xAI Grok ----
  // Rates from docs.x.ai (2026-09), ≤200k-token tier only — the >200k prompt tier is not
  // modeled yet (it would be a `long_prompt` rate set, as on Haiku 5.5). grok-4.5–4.7 $2/$6
  // (cached input $0.50 on 4.6/4.7, $0.30 on 4.5); grok-4.3 + the grok-4.20 variants
  // $1.25/$2.50 (cached $0.20). Bare keys: getModelPricing strips the `x-ai/` prefix, so
  // these also price OpenRouter-routed `x-ai/grok-*` usage.
  'grok-4.7':                     { input: 2,    output: 6,   cache_read: 0.50 },
  'grok-4.6':                     { input: 2,    output: 6,   cache_read: 0.50 },
  'grok-4.5':                     { input: 2,    output: 6,   cache_read: 0.30 },
  'grok-4.3':                     { input: 1.25, output: 2.5, cache_read: 0.20 },
  'grok-4.20-0309-reasoning':     { input: 1.25, output: 2.5, cache_read: 0.20 },
  'grok-4.20-0309-non-reasoning': { input: 1.25, output: 2.5, cache_read: 0.20 },
  'grok-4.20-multi-agent-0309':   { input: 1.25, output: 2.5, cache_read: 0.20 },

  // ---- TypeSafe decisions models ----
  // Rates from the OpenRouter model pages for typesafe/jev-latest and
  // typesafe/jev-1.13, and TypeSafe's own models page (docs.typesafe.ai/models)
  // for its direct ids: $0.042/MTok input, output free. An OpenRouter response
  // prices the call it served in `usage.cost`, which the caller that records cost
  // takes over this table; a direct TypeSafe response reports no cost, so this
  // table prices every call on a TypeSafe connection.
  'jev-latest':  { input: 0.042, output: 0 },
  'jev-preview': { input: 0.042, output: 0 },
  'jev-1.14.0':  { input: 0.042, output: 0 },
  'jev-1.13.0':  { input: 0.042, output: 0 },
  'jev-1.13':    { input: 0.042, output: 0 },
};

/**
 * Resolve the rates in force at `atMs` (default: now) for a model. Strips OpenRouter-style
 * provider prefixes (`anthropic/claude-sonnet-4-6` → `claude-sonnet-4-6`).
 *
 * Returns `null` for unknown models. Callers should record cost = 0 in that
 * case. The optional `onMiss` callback fires every time — caller-side
 * deduplication (Sentry fingerprinting, log aggregation) is preferred over
 * an in-memory Set here because module state is per-isolate in Workers and
 * agent settings allow arbitrary model strings, so an in-memory Set could
 * grow unboundedly per isolate.
 */
export function getModelPricing(
  model: string | null | undefined,
  onMiss?: (normalizedModel: string) => void,
  atMs: number = Date.now(),
): ModelPricing | null {
  if (!model) return null;
  const normalized = model.includes('/') ? model.split('/').slice(1).join('/') : model;
  const pricing = MODEL_PRICING[normalized];
  if (!pricing) {
    if (onMiss) onMiss(normalized);
    return null;
  }
  return ratesAt(pricing, atMs);
}

/**
 * USD cost breakdown for a single (model, usage) pair.
 * All amounts are in dollars (not cents). Returns zeros across the board
 * when the model has no pricing entry — callers may detect this with the
 * companion `getModelPricing(...) === null` check if they need to distinguish
 * "$0 because nothing happened" from "$0 because we don't know the price".
 */
export interface CostBreakdownUsd {
  input: number;
  output: number;
  cache_read: number;
  cache_write: number;
  total: number;
}

/**
 * The long-prompt slice of ONE call's usage: its counters when its own prompt (uncached input +
 * cache reads + cache writes) exceeds its model's long-prompt threshold, else undefined. Call it
 * on each call as it returns and sum the slices with the usage; {@link computeCostUsd} then
 * prices the slice at the long-prompt rates and the rest at the base rates. `atMs` as there.
 */
export function longPromptSlice(
  model: string | null | undefined,
  usage: LlmTokenCounts,
  atMs: number = Date.now(),
): LongPromptUsage | undefined {
  const threshold = getModelPricing(model, undefined, atMs)?.long_prompt?.above_prompt_tokens;
  if (threshold === undefined) return undefined;
  const slice = countsOf(usage);
  const prompt = slice.input_tokens + slice.cache_read_input_tokens + slice.cache_creation_input_tokens;
  return prompt > threshold ? slice : undefined;
}

/** The counter-wise sum of two slices; absent on both sides stays absent. */
export function sumLongPromptUsage(
  a: LongPromptUsage | undefined,
  b: LongPromptUsage | undefined,
): LongPromptUsage | undefined {
  if (!a || !b) return a ?? b;
  const sum = countsOf({});
  for (const key of COUNTER_KEYS) sum[key] = a[key] + b[key];
  return sum;
}

/** Every counter as a finite non-negative number (absent, negative or non-numeric → 0). */
function countsOf(usage: LlmTokenCounts): TokenCounts {
  const counts = {} as TokenCounts;
  for (const key of COUNTER_KEYS) {
    const v: unknown = usage[key];
    counts[key] = typeof v === 'number' && Number.isFinite(v) ? Math.max(0, v) : 0;
  }
  return counts;
}

function priceCounts(pricing: Omit<ModelPricing, 'long_prompt'>, usage: TokenCounts): CostBreakdownUsd {
  const i = usage.input_tokens * pricing.input / 1_000_000;
  const o = usage.output_tokens * pricing.output / 1_000_000;
  const r = usage.cache_read_input_tokens * (pricing.cache_read ?? 0) / 1_000_000;

  // Cache writes split by TTL: 5m bills at 1.25x input, 1h at 2x. Anthropic's
  // `cache_creation_input_tokens` is the SUM of its per-TTL buckets, so only the
  // 1h count is carried and the 5m portion is DERIVED as the remainder. That is
  // what makes an unpriced token unrepresentable: any bucket the provider didn't
  // break out (a row predating the split, a provider reporting no breakdown, a
  // future third TTL) stays inside the aggregate and prices at the 5m rate
  // instead of silently costing nothing. Tracking 5m as its own field allowed a
  // partially-reported split to drop the remainder and price BELOW the unsplit
  // fallback, which is the defect this shape removes rather than guards against.
  const rate5m = pricing.cache_write ?? 0;
  // A provider publishing no separate 1h rate falls back to its 5m rate rather
  // than pricing 1h writes at zero (see the catalog invariant in the tests).
  const rate1h = pricing.cache_write_1h ?? rate5m;
  const tokens1h = usage.cache_creation_1h_input_tokens;
  // Floor at 0: a 1h count exceeding the aggregate (inconsistent provider
  // payload) must not make the remainder negative and credit cost back.
  const tokens5m = Math.max(0, usage.cache_creation_input_tokens - tokens1h);
  const w = (tokens5m * rate5m + tokens1h * rate1h) / 1_000_000;

  return { input: i, output: o, cache_read: r, cache_write: w, total: i + o + r + w };
}

/**
 * USD cost for one call, or for a stored row summing several. `long_prompt` is the summed slice
 * of the calls that crossed the model's long-prompt threshold ({@link longPromptSlice}); it is
 * part of the totals, not added to them, and prices at the long-prompt rates while the rest
 * prices at the base rates. A model with no long-prompt tier prices it at base rates, so no
 * token goes unpriced. `atMs` is when it is priced (default: now), which picks between a
 * model's current rates and a published `next` change.
 */
export function computeCostUsd(
  model: string | null | undefined,
  usage: LlmTokenCounts & { long_prompt?: LlmTokenCounts },
  onMiss?: (normalizedModel: string) => void,
  atMs: number = Date.now(),
): CostBreakdownUsd {
  const pricing = getModelPricing(model, onMiss, atMs);
  if (!pricing) {
    return { input: 0, output: 0, cache_read: 0, cache_write: 0, total: 0 };
  }
  const totals = countsOf(usage);
  if (!pricing.long_prompt || !usage.long_prompt) return priceCounts(pricing, totals);

  // Clamped to the totals: a slice is a part of them, so an inconsistent one must neither
  // price a token twice nor leave the base remainder negative.
  const slice = countsOf(usage.long_prompt);
  const base = { ...totals };
  for (const key of COUNTER_KEYS) {
    slice[key] = Math.min(slice[key], totals[key]);
    base[key] = totals[key] - slice[key];
  }
  const b = priceCounts(pricing, base);
  const l = priceCounts(pricing.long_prompt, slice);
  return {
    input: b.input + l.input,
    output: b.output + l.output,
    cache_read: b.cache_read + l.cache_read,
    cache_write: b.cache_write + l.cache_write,
    total: b.total + l.total,
  };
}

// ---------------------------------------------------------------------------
// STT (Speech-to-Text) pricing — USD per second of input audio
// ---------------------------------------------------------------------------

export interface SttPricing {
  /** USD per second of audio transcribed. */
  per_second: number;
}

// Keyed `<service_name>/<model>`. OpenAI: $0.006/min whisper-1, $0.003/min gpt-4o-mini-transcribe (snapshot 2026-01). Groq: $0.04/hour whisper-large-v3-turbo (2026-01). ElevenLabs: $0.22/hour scribe_v2 batch (2026-07).
export const STT_PRICING: Record<string, SttPricing> = {
  'Openai/whisper-1':                 { per_second: 0.006 / 60 },
  'Openai/gpt-4o-mini-transcribe':    { per_second: 0.003 / 60 },
  'Groq/whisper-large-v3-turbo':      { per_second: 0.04 / 3600 },
  'ElevenLabs/scribe_v2':             { per_second: 0.22 / 3600 },
};

export function computeSttCostUsd(
  provider: string | null | undefined,
  model: string | null | undefined,
  seconds: number | null | undefined,
  onMiss?: (key: string) => void,
): number {
  if (!provider || !model || !seconds || seconds <= 0) return 0;
  const key = `${provider}/${model}`;
  const pricing = STT_PRICING[key];
  if (!pricing) {
    if (onMiss) onMiss(key);
    return 0;
  }
  return seconds * pricing.per_second;
}

// ---------------------------------------------------------------------------
// TTS (Text-to-Speech) pricing — per input character, or per token
// ---------------------------------------------------------------------------

/** A model billed by input characters. */
export interface TtsCharRates {
  /** USD per 1,000,000 input characters. */
  per_million_chars: number;
}

/** A model billed by tokens: text in, audio out. */
export interface TtsTokenRates {
  /** USD per 1,000,000 text input tokens. */
  per_million_input_tokens: number;
  /** USD per 1,000,000 audio output tokens. */
  per_million_output_tokens: number;
}

export type TtsRates = TtsCharRates | TtsTokenRates;

/** A TTS model's rates, plus any published price change (see WithPriceChange). */
export type TtsPricing = WithPriceChange<TtsRates>;

/** What one synthesis consumed, in each unit a TTS provider bills by. */
export interface TtsBilledUsage {
  /** Input characters — the provider-billed count where the provider reports one. */
  characters: number;
  /** Text tokens in, as the provider reported them. Token-billed models only. */
  input_tokens?: number;
  /** Audio tokens out, as the provider reported them. Token-billed models only. */
  output_tokens?: number;
}

/**
 * A provider-reported token count, or undefined when the provider did not report one (never
 * 0 in its place: a missing count is unpriceable, a zero is free). Used both where a synthesis
 * reads its provider's response and where the cost snapshot reads the stamped count back.
 */
export function reportedTokenCount(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.round(value) : undefined;
}


// Keyed `<service_name>/<model>`. Snapshot 2026-01. Note: OpenAI/Groq count input chars; ElevenLabs counts billed (post-normalization) chars — caller passes the unit the provider charges for.
// Google bills per token (25 audio tokens per second of speech) (ai.google.dev/gemini-api/docs/pricing, 2026-09).
export const TTS_PRICING: Record<string, TtsPricing> = {
  'Openai/tts-1':                            { per_million_chars: 15 },
  'Openai/tts-1-hd':                         { per_million_chars: 30 },
  'Openai/gpt-4o-mini-tts':                  { per_million_chars: 12 },
  'Groq/canopylabs/orpheus-v1-english':      { per_million_chars: 40 },
  'Groq/canopylabs/orpheus-arabic-saudi':    { per_million_chars: 40 },
  'ElevenLabs/eleven_flash_v2_5':            { per_million_chars: 50 },
  'ElevenLabs/eleven_turbo_v2_5':            { per_million_chars: 50 },
  'ElevenLabs/eleven_v3':                    { per_million_chars: 100 },
  'ElevenLabs/eleven_multilingual_v2':       { per_million_chars: 100 },
  'Google/gemini-3.8-flash-tts': {
    per_million_input_tokens: 0.5, per_million_output_tokens: 9,
    next: { effective: GOOGLE_PRICE_CHANGE, per_million_input_tokens: 1, per_million_output_tokens: 18 },
  },
  'Google/gemini-3.8-flash-lite-tts': {
    per_million_input_tokens: 0.5, per_million_output_tokens: 6,
    next: { effective: GOOGLE_PRICE_CHANGE, per_million_input_tokens: 1, per_million_output_tokens: 12 },
  },
};

/**
 * USD for one synthesis. `atMs` is when it ran (default: now), which picks between a model's
 * current rates and a published `next` change.
 *
 * A token-billed model whose synthesis reported no token counts cannot be priced from its
 * characters: that reports `<key>:missing-tokens` through `onMiss` and prices at 0, the same
 * way an unknown model does.
 */
export function computeTtsCostUsd(
  provider: string | null | undefined,
  model: string | null | undefined,
  usage: TtsBilledUsage | null | undefined,
  onMiss?: (key: string) => void,
  atMs: number = Date.now(),
): number {
  if (!provider || !model || !usage || !(usage.characters > 0)) return 0;
  const key = `${provider}/${model}`;
  const pricing = TTS_PRICING[key];
  if (!pricing) {
    if (onMiss) onMiss(key);
    return 0;
  }
  const rates = ratesAt<TtsRates>(pricing, atMs);
  if ('per_million_chars' in rates) return usage.characters * rates.per_million_chars / 1_000_000;
  if (usage.output_tokens === undefined) {
    if (onMiss) onMiss(`${key}:missing-tokens`);
    return 0;
  }
  return ((usage.input_tokens ?? 0) * rates.per_million_input_tokens
    + usage.output_tokens * rates.per_million_output_tokens) / 1_000_000;
}

// ---------------------------------------------------------------------------
// Container / code-execution pricing — USD estimate from per-response counters
// ---------------------------------------------------------------------------
//
// These are user-side costs (BYOK): the customer pays their own provider account
// directly. We surface the estimate so users can see container spend per
// conversation and set per-conversation counter limits — it does NOT contribute
// to WayAI quota.
//
// Precision caveats:
//   Anthropic — bills $0.05/active-container-hour but the Messages API exposes
//     only `code_execution_requests` (per-response counter), not hours. We
//     estimate by assuming each request occupies the container for ~10s; real
//     cost may differ when requests cluster densely or sparsely.
//   OpenAI — bills $0.03 / $0.12 / $0.48 / $1.92 per 20-min code-interpreter
//     session (1GB / 4GB / 16GB / 64GB tiers). The response doesn't surface
//     the tier, so we estimate at the 1GB floor; heavier-memory sessions cost
//     more. A future `/usage` reconciliation cron can write true-up corrections.

export interface ContainerPricing {
  /** Anthropic estimate per `code_execution_requests` count, in USD. */
  anthropic_per_request_usd: number;
  /** OpenAI estimate per new `container_sessions` count, in USD (1GB floor). */
  openai_per_session_usd: number;
}

// Snapshot 2026-05.
export const CONTAINER_PRICING: ContainerPricing = {
  anthropic_per_request_usd: 0.05 * 10 / 3600, // $0.05/hr × ~10s/request ≈ $0.000139
  openai_per_session_usd: 0.03,                 // 1GB tier, 20-min session
};

export function computeContainerCostUsd(
  provider: 'anthropic' | 'openai' | null | undefined,
  codeExecutionRequests: number | null | undefined,
  containerSessions: number | null | undefined,
): number {
  switch (provider) {
    case 'anthropic':
      return Math.max(0, codeExecutionRequests || 0) * CONTAINER_PRICING.anthropic_per_request_usd;
    case 'openai':
      return Math.max(0, containerSessions || 0) * CONTAINER_PRICING.openai_per_session_usd;
    default:
      return 0;
  }
}
